// /src/components/SalesContainer.js
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../context/CartContext';
import { useFilter } from "../context/FilterContext";
import '../styles/SalesContainer.css';

const categoryKeywords = {
  Leaves: ['leaves'],
  Fruits: ['fruit'],
  Grains: ['grain'],
  Protein: ['protein'],
  Spices: ['spice'],
  Snacks: ['snack'],
  Drinks: ['drink'],
  Nuts: ['nut'],
  Others: []
};

function SalesContainer() {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const { activeCategory, searchQuery, setActiveCategory, setSearchQuery } = useFilter();
  const [error, setError] = useState(false);
  const { addToCart } = useCart();
  const [feedback, setFeedback] = useState({});
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // fetch products
  useEffect(() => {
    fetch(`${API_BASE_URL}/products`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
        return res.json();
      })
      .then(data => {
        setProducts(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(err => {
        setError(true);
        console.error('Failed to load products:', err);
        setProducts([]);
        setLoading(false);
      });
  }, [API_BASE_URL]);

  // helper to decide if product matches category
  const matchesCategory = (product, category) => {
    if (!category || category === 'All') return true;
    const keywords = categoryKeywords[category] || [];
    const catField = (product.category || '').toLowerCase();
    const nameField = (product.name || '').toLowerCase();

    if (catField.includes(category.toLowerCase())) return true;
    return keywords.some(kw => catField.includes(kw) || nameField.includes(kw));
  };

  // helper to decide if product matches search query
  const matchesSearch = (product, query) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      (product.name || '').toLowerCase().includes(q) ||
      (product.description || '').toLowerCase().includes(q) ||
      (product.category || '').toLowerCase().includes(q)
    );
  };

  // filter products when category/search changes
  useEffect(() => {
    if (!products || products.length === 0) {
      setFilteredProducts([]);
      return;
    }
    setFilteredProducts(
      products.filter(
        product =>
          matchesCategory(product, activeCategory) &&
          matchesSearch(product, searchQuery)
      )
    );
  }, [products, activeCategory, searchQuery]);

  const handleAdd = async (product) => {
    const result = await addToCart(product);
    setFeedback(prev => ({
      ...prev,
      [product._id]: { message: result.message, success: result.success }
    }));
    setTimeout(() => {
      setFeedback(prev => {
        const copy = { ...prev };
        delete copy[product._id];
        return copy;
      });
    }, 2000);
  };

  return (
    <div className="store-catalog store-shell">
      <div className="catalog-heading"><div><span className="hero-eyebrow">FILL YOUR BASKET</span><h2>Explore our grocery store</h2><p>Everyday essentials. The flavours you love.</p></div><span className="catalog-count" role="status">{loading ? 'Loading groceries…' : `${filteredProducts.length} products`}</span></div>
      <div className="catalog-categories" aria-label="Filter products by category">{['All', ...Object.keys(categoryKeywords)].map(category => <button type="button" key={category} aria-pressed={activeCategory === category} onClick={() => setActiveCategory(category)} className={activeCategory === category ? 'active' : ''}>{category === 'All' ? 'All groceries' : category}</button>)}</div>
      {searchQuery && <div className="catalog-search-state">Results for “{searchQuery}”<button type="button" onClick={() => setSearchQuery('')}>Clear search</button></div>}
      <div className="store-product-grid" aria-busy={loading}>
        {loading && Array.from({ length: 8 }, (_, i) => <div key={i} className="product-skeleton" aria-hidden="true"><div /><span /><span /></div>)}
        <AnimatePresence>
          {filteredProducts.map((product) => (
            <motion.div
              key={product._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.4 }}
              className="store-product-card relative bg-white border border-slate-100 p-3 shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col group"
            >
              {/* Product Image Area */}
              <div className="relative w-full rounded-2xl bg-slate-50/80 flex items-center justify-center p-3 aspect-square overflow-hidden mb-3">
                <Link to={`/product/${product._id}`} className="w-full h-full flex items-center justify-center">
                  <img
                    src={`${product.image}?w=200&h=200&c=fill&f=webp`}
                    srcSet={`
                      ${product.image}?w=200&h=200&c=fill&f=webp 200w,
                      ${product.image}?w=400&h=400&c=fill&f=webp 400w,
                      ${product.image}?w=800&h=800&c=fill&f=webp 800w
                    `}
                    sizes="(max-width: 600px) 200px, (max-width: 900px) 400px, 800px"
                    alt={product.name}
                    loading="lazy"
                    className={`w-full h-full object-contain group-hover:scale-105 transition-transform duration-500 ${
                      product.stock === 0 ? "filter grayscale" : ""
                    }`}
                  />
                </Link>

                {/* Stock badges */}
                {product.stock === 0 ? (
                  <div className="absolute top-3 left-3 bg-slate-900/90 text-white font-extrabold text-[9px] uppercase tracking-widest px-2.5 py-1.5 rounded-full backdrop-blur-sm z-10 shadow-sm">
                    Sold Out
                  </div>
                ) : (
                  <div className="absolute top-3 left-3 bg-green-700 text-white font-extrabold text-[9px] uppercase tracking-widest px-2.5 py-1.5 rounded-full z-10 shadow-sm">
                    Fresh
                  </div>
                )}

                {/* Micro-feedback overlay inside image box */}
                <AnimatePresence>
                  {feedback[product._id] && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      className={`absolute inset-0 m-3 rounded-2xl flex items-center justify-center p-4 text-center text-xs font-black z-20 backdrop-blur-md ${
                        feedback[product._id].success 
                          ? 'bg-green-700/80 text-white' 
                          : 'bg-red-800/80 text-white'
                      }`}
                    >
                      <div className="flex flex-col items-center gap-1">
                        <i className={`bx text-lg ${feedback[product._id].success ? 'bx-check-circle' : 'bx-error-circle'}`} />
                        <span>{feedback[product._id].message}</span>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Product Info */}
              <div className="flex flex-col flex-1 px-1">
                <span className="text-[10px] font-black uppercase text-amber-600 tracking-widest font-[Montserrat]">
                  {product.category || "Produce"}
                </span>
                
                <Link to={`/product/${product._id}`}>
                  <h3 className="text-base font-bold text-slate-800 mt-1 line-clamp-2 group-hover:text-green-700 transition-colors font-[Montserrat]">
                    {product.name}
                  </h3>
                </Link>
                
                <p className="text-sm text-slate-500 line-clamp-2 mt-1 mb-3 leading-relaxed">
                  {product.description || "Fresh selection pick"}
                </p>

                {/* Bottom Row */}
                <div className="mt-auto pt-3 border-t border-slate-50 flex items-center justify-between">
                  <span className="text-[17px] font-black text-green-700 font-[Poppins]">
                    ${Number(product.price).toFixed(2)}
                  </span>

                  <motion.button
                    whileHover={product.stock > 0 ? { scale: 1.05 } : {}}
                    whileTap={product.stock > 0 ? { scale: 0.95 } : {}}
                    type="button"
                    aria-label={`Add ${product.name} to cart`}
                    className={`text-[11px] px-3.5 py-1.5 rounded-full font-bold transition-all focus:outline-none flex items-center gap-1 shadow-sm
                      ${
                        product.stock === 0
                          ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                          : 'bg-green-700 text-white hover:bg-green-800'
                      }`}
                    onClick={() => product.stock > 0 && handleAdd(product)}
                    disabled={product.stock === 0}
                  >
                    {product.stock === 0 ? (
                      <>
                        <i className="bx bx-lock-alt text-xs" />
                        <span>Unavailable</span>
                      </>
                    ) : (
                      <>
                        <i className="bx bx-cart-add text-sm" />
                        <span>Add</span>
                      </>
                    )}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {!loading && filteredProducts.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="catalog-empty text-center py-12"
          >
            <i className="bx bx-search-alt text-4xl text-slate-300" />
            <p className="text-slate-500 mt-3 font-semibold font-[Poppins]">
              {error ? 'We couldn’t load the groceries. Please try again.' : 'No products match your filters'}
            </p>
            <button type="button" className="store-button" onClick={() => { if (error) window.location.reload(); else { setActiveCategory('All'); setSearchQuery(''); } }}>{error ? 'Try again' : 'Show all groceries'}</button>
          </motion.div>
        )}
      </div>
    </div>
  );
}

export default SalesContainer;
